"use client";

import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { useEffect, type ReactNode } from "react";
import { ThemeProvider } from "@/components/theme-provider";
import {
  assertProviderListsAreDisjoint,
  routeNeedsClerk,
  routeNeedsCustomerAuth,
} from "@/lib/customer-auth/route-providers";

const AuthenticatedProviders = dynamic(
  () =>
    import("@/components/authenticated-providers").then(
      (mod) => mod.AuthenticatedProviders,
    ),
  { ssr: false },
);
const PublicConvexProvider = dynamic(() =>
  import("@/components/public-convex-provider").then(
    (mod) => mod.PublicConvexProvider,
  ),
);
const CustomerAuthProvider = dynamic(
  () =>
    import("@/components/customer-auth/customer-auth-provider").then(
      (mod) => mod.CustomerAuthProvider,
    ),
  { ssr: false },
);

const CLIENT_CONVEX_PATH_PREFIXES = [
  "/blog",
  // /catering reads the tenant's live catering-ordering config for its
  // primary "Order Catering" CTA (api.cateringSettings.getPublic).
  "/catering",
  "/events",
  // QR giveaway entry page (api.events.getActiveGiveaway + eventRsvps.create).
  "/giveaway",
  "/jobs",
  "/menu",
  "/order",
  "/redeem",
  "/reservations",
  // The offer page embeds the same first-party reservation form.
  "/dinner-for-two",
  // Guest tipping. The page calls useAction/useQuery during SSR, so without
  // this it renders its shell and then throws "Could not find Convex client!"
  // and the whole payment surface 500s.
  "/tip",
  "/tenant/billing/activate",
  "/unsubscribe",
] as const;

/**
 * Route-aware providers. Static public pages only need theme hydration; routes
 * with Convex hooks opt into the lighter unauthenticated Convex provider, while
 * admin/auth/checkout routes keep Clerk + Convex and the Cove Club account
 * surfaces get the first-party customer provider.
 */
export function ConvexThemeProviders({ children }: { children: ReactNode }) {
  const pathname = usePathname();

  // The ops-shell-init script in src/app/layout.tsx only runs on document load,
  // so a client-side nav from an operations route to a public one would strand
  // data-ops-shell and force a neutral body onto a restaurant page. This owns
  // the attribute after hydration; keep the prefix list identical to that
  // script and to the html[data-ops-shell] rule in src/app/ops-theme.css.
  //
  // The list is the set of routes whose layout imports admin/admin-theme.css
  // (admin, app, checklists, demo, onboarding, ops, tenant). checklists, demo and
  // onboarding carry .admin-theme below <html> instead of on it, so the
  // attribute is what activates the html[data-ops-shell]:has(.admin-theme) root
  // and portal token scope there. This regex must stay byte-identical to the
  // emitted one in layout.tsx — a divergence sets the attribute pre-paint and
  // strips it on hydration, which flashes.
  useEffect(() => {
    const isOpsShell =
      /^\/(admin|app|checklists|demo|onboarding|ops|partners|tenant)(\/|$)/.test(
        pathname ?? "",
      );
    const root = document.documentElement;
    if (isOpsShell) {
      root.setAttribute("data-ops-shell", "");
    } else {
      root.removeAttribute("data-ops-shell");
    }
  }, [pathname]);
  const isPublicSurface =
    !pathname?.startsWith("/admin") &&
    !pathname?.startsWith("/app") &&
    !pathname?.startsWith("/ops") &&
    !pathname?.startsWith("/checklists") &&
    !pathname?.startsWith("/partners") &&
    !pathname?.startsWith("/tenant") &&
    !pathname?.startsWith("/sign-in") &&
    !pathname?.startsWith("/sign-up");
  assertProviderListsAreDisjoint(pathname);
  const needsClerk = routeNeedsClerk(pathname);
  const needsCustomerAuth = routeNeedsCustomerAuth(pathname);
  const needsClientConvex = CLIENT_CONVEX_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname?.startsWith(`${prefix}/`),
  );
  const allowsPublicThemeToggle =
    pathname === "/onboarding" ||
    pathname?.startsWith("/onboarding/") ||
    pathname === "/demo/ai-marketing";
  const forcedTheme =
    isPublicSurface && !allowsPublicThemeToggle ? "light" : undefined;

  if (needsCustomerAuth) {
    return (
      <CustomerAuthProvider>
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          forcedTheme={forcedTheme}
          storageKey="theme-preference"
        >
          {children}
        </ThemeProvider>
      </CustomerAuthProvider>
    );
  }

  if (!needsClerk && !needsClientConvex) {
    return (
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        forcedTheme={forcedTheme}
        storageKey="theme-preference"
      >
        {children}
      </ThemeProvider>
    );
  }

  if (!needsClerk) {
    return (
      <PublicConvexProvider>
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          forcedTheme={forcedTheme}
          storageKey="theme-preference"
        >
          {children}
        </ThemeProvider>
      </PublicConvexProvider>
    );
  }

  return (
    <AuthenticatedProviders forcedTheme={forcedTheme}>
      {children}
    </AuthenticatedProviders>
  );
}
